import { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, Line, ComposedChart, LabelList, ReferenceLine,
} from 'recharts';
import type { FinanceSummaryRow } from '../../types/finance';
import { SERIE, pesos, pesosCortos, type Porcion } from '../../utils/financeCharts';

/**
 * FE-32 — Gráficos de Finanzas. Las tres series tienen identidad FIJA en toda
 * la app (tiles, tabla, gráficos): ingresos verde, gastos del vehículo ámbar,
 * gastos de operación violeta. Los valores de los tokens `--fin-*` viven en
 * index.css, con un paso distinto por tema (validados para daltonismo en los
 * dos fondos). El importe que se LEE sale de `formatearCOP` sobre el texto
 * del servidor; el `number` solo dibuja.
 */

const TOOLTIP_STYLE = { background: 'var(--bg-elevated)', border: '1px solid var(--border-strong)', borderRadius: '8px', color: 'var(--text-primary)', fontSize: 12 };
const AXIS_TICK = { fontSize: 11, fill: 'var(--text-muted)' };
const GRID = 'var(--border)';

/** recharts 3 entrega `activeIndex` (número o texto) en el clic del gráfico: la fila es `data[índice]`. */
function indiceActivo<T>(idx: unknown, data: T[]): T | undefined {
  const n = typeof idx === 'number' ? idx : typeof idx === 'string' ? Number(idx) : NaN;
  return Number.isInteger(n) ? data[n] : undefined;
}

function etiquetaMes(key: string, label: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(key);
  if (!m) return label;
  return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString('es-CO', { month: 'short', year: '2-digit' });
}

// ── Tendencia mensual ──────────────────────────────────────────────────────

export function TendenciaMensual({ rows, onElegirMes }: { rows: FinanceSummaryRow[]; onElegirMes?: (key: string) => void }) {
  const data = useMemo(() => rows.map(r => ({
    key: r.key,
    label: etiquetaMes(r.key, r.label),
    ingresos: Number(r.income),
    vehiculo: Number(r.expensesVehicle),
    operacion: Number(r.expensesOperation),
    resultado: Number(r.result),
  })), [rows]);

  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart data={data} margin={{ top: 8, right: 12, left: 8, bottom: 0 }} barGap={2} barCategoryGap="28%"
        onClick={st => { const k = indiceActivo(st?.activeIndex, data)?.key; if (k && onElegirMes) onElegirMes(k); }}
        style={{ cursor: onElegirMes ? 'pointer' : 'default' }}>
        <CartesianGrid vertical={false} stroke={GRID} />
        <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false} />
        <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} width={64} tickFormatter={pesosCortos} />
        <ReferenceLine y={0} stroke="var(--border-strong)" />
        <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'var(--accent-glow)' }} labelStyle={{ color: 'var(--text-muted)', marginBottom: 4 }}
          formatter={(v, name) => [pesos(Number(v)), String(name)]} />
        <Legend iconType="circle" iconSize={8} formatter={v => <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{v}</span>} />
        <Bar onClick={(_, i) => { const k = data[i]?.key; if (k && onElegirMes) onElegirMes(k); }} dataKey="ingresos" name="Ingresos" fill={SERIE.income} radius={[4, 4, 0, 0]} maxBarSize={28} />
        <Bar onClick={(_, i) => { const k = data[i]?.key; if (k && onElegirMes) onElegirMes(k); }} dataKey="vehiculo" name="Gastos del vehículo" fill={SERIE.vehicle} radius={[4, 4, 0, 0]} maxBarSize={28} />
        <Bar onClick={(_, i) => { const k = data[i]?.key; if (k && onElegirMes) onElegirMes(k); }} dataKey="operacion" name="Gastos de operación" fill={SERIE.operation} radius={[4, 4, 0, 0]} maxBarSize={28} />
        <Line type="monotone" dataKey="resultado" name="Resultado" stroke={SERIE.result} strokeWidth={2} dot={{ r: 4, strokeWidth: 2, fill: 'var(--bg-surface)' }} activeDot={{ r: 6 }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ── Reparto (dona) ─────────────────────────────────────────────────────────

export function Reparto({ data, centro, centroLabel, onElegir }: { data: Porcion[]; centro: string; centroLabel: string; onElegir?: (key: string) => void }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 200px) 1fr', gap: '0.75rem', alignItems: 'center' }} className="finance-reparto">
      <div style={{ position: 'relative', height: 190 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={56} outerRadius={82} paddingAngle={2} dataKey="value" nameKey="label" stroke="var(--bg-surface)" strokeWidth={2}
              onClick={(_, i) => { const k = data[i]?.key; if (k && k !== '__otros' && onElegir) onElegir(k); }} style={{ cursor: onElegir ? 'pointer' : 'default' }}>
              {data.map(d => <Cell key={d.key} fill={d.color} />)}
            </Pie>
            <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v, name) => [`${pesos(Number(v))} · ${total ? Math.round((Number(v) / total) * 100) : 0} %`, String(name)]} />
          </PieChart>
        </ResponsiveContainer>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', textAlign: 'center' }}>
          <span style={{ fontSize: centro.length > 9 ? 14 : 18, fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{centro}</span>
          <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>{centroLabel}</span>
        </div>
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.3rem', minWidth: 0 }}>
        {data.map(d => (
          <li key={d.key} onClick={() => { if (d.key !== '__otros' && onElegir) onElegir(d.key); }}
            style={{ display: 'grid', gridTemplateColumns: '10px 1fr auto auto', gap: '0.5rem', alignItems: 'center', fontSize: 12, cursor: onElegir && d.key !== '__otros' ? 'pointer' : 'default' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: d.color }} />
            <span style={{ color: 'var(--text-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
            <span style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{pesosCortos(d.value)}</span>
            <span style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums', minWidth: 34, textAlign: 'right' }}>{total ? Math.round((d.value / total) * 100) : 0} %</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Ranking de resultado (barras horizontales, divergentes) ────────────────

export function RankingResultado({ rows, onElegir, vacioLabel = 'Sin asignar' }: { rows: FinanceSummaryRow[]; onElegir?: (key: string) => void; vacioLabel?: string }) {
  const data = useMemo(() => rows
    .map(r => ({ key: r.key, label: r.key ? r.label : vacioLabel, resultado: Number(r.result), ingresos: Number(r.income), gastos: Number(r.expenses) }))
    .sort((a, b) => b.resultado - a.resultado), [rows, vacioLabel]);
  return (
    <ResponsiveContainer width="100%" height={Math.max(120, data.length * 34 + 24)}>
      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 72, left: 0, bottom: 0 }}
        onClick={st => { const k = indiceActivo(st?.activeIndex, data)?.key; if (k && onElegir) onElegir(k); }}>
        <XAxis type="number" hide domain={[(min: number) => Math.min(0, min), (max: number) => Math.max(0, max)]} />
        <YAxis type="category" dataKey="label" width={110} tickLine={false} axisLine={false} tick={{ ...AXIS_TICK, fill: 'var(--text-secondary)' }}
          tickFormatter={(l: string) => (l.length > 16 ? `${l.slice(0, 15)}…` : l)} />
        <ReferenceLine x={0} stroke="var(--border-strong)" />
        <Tooltip cursor={{ fill: 'var(--accent-glow)' }} contentStyle={TOOLTIP_STYLE}
          formatter={(v, name) => [pesos(Number(v)), String(name)]} />
        <Bar dataKey="resultado" name="Resultado" radius={4} barSize={16} style={{ cursor: onElegir ? 'pointer' : 'default' }} onClick={(_, i) => { const k = data[i]?.key; if (k && onElegir) onElegir(k); }}>
          {data.map(d => <Cell key={d.key || '__sin'} fill={d.resultado < 0 ? SERIE.negative : SERIE.income} />)}
          <LabelList dataKey="resultado" position="right" formatter={(v: unknown) => pesosCortos(Number(v))} style={{ fontSize: 11, fill: 'var(--text-secondary)' }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Para la tabla: ingresos vs gastos de una fila como barra apilada, a escala del máximo del eje. */
export function BarraComparada({ ingresos, gastos, max }: { ingresos: number; gastos: number; max: number }) {
  const w = (v: number) => (max > 0 ? `${Math.max(0, Math.min(100, (v / max) * 100))}%` : '0%');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 80 }} aria-hidden>
      <div style={{ height: 5, borderRadius: 3, background: 'var(--bg-raised)' }}><div style={{ height: '100%', width: w(ingresos), borderRadius: 3, background: SERIE.income }} /></div>
      <div style={{ height: 5, borderRadius: 3, background: 'var(--bg-raised)' }}><div style={{ height: '100%', width: w(gastos), borderRadius: 3, background: SERIE.vehicle }} /></div>
    </div>
  );
}
